import React, { useEffect, useRef, useState } from 'react';
import { HiArrowLeft, HiLink, HiPencilAlt } from 'react-icons/hi';
import { FiAlertTriangle } from 'react-icons/fi';
import { DashboardStatus, dashboardStatusHexColors, getDashboardStatusColor, getDashboardStatusLabel } from "../../constants/integrationTypes";
import Resources from "../Projects/sections/Project/sections/ProjectBoxComponent/Resources";
import { Badge, Tooltip } from "flowbite-react";
import { useSelector } from "react-redux";
import { useJwt } from "../../helpers/common";
import { ENTITY } from "../../redux/slices/timeline";
import { AnimatedNumber as AnimatedNumberShared, CrossfadeText as CrossfadeTextShared } from "../../components/common/Animated";
import TruncatedText from '../../components/common/TruncatedText';
import CustomTooltip from '../../components/common/CustomTooltip';
import { getWorkItemBreakdownTooltipContent } from '../Projects/sections/Project/sections/WorkItemBreakdownTooltip';
import { ViewTitle } from '../../features/StrategyMapView/components/ViewTitle/ViewTitle';
import RiskStatusDropdown from '../../components/common/RiskStatusDropdown';
import DashboardStatusBadge from '../../components/common/DashboardStatusBadge';

type TopNavBarProps = {
  id?: string;
  title?: string;
  progress?: number;
  totalTasks?: number;
  status?: string;
  cost: number;
  simFlag?: boolean;
  sim_data?: {
    progress?: number;
    status?: string;
    stats?: any;
  };
  childEntityType?: string;
  childEntityTypes?: Record<string, number> | null;
  members: string[];
  skills?: Object[];
  duration_with_delay?: string;
  handleGoBack: () => void;
  setShowSkillsModal: any;
  isLoading?: boolean;
  entityType?: string;
  stats?: any;

  integration_data?: {
    integration_id: string;
    integration_type: string;
    integration_url: string;
    integration_item_type: string;
    integration_status: string;
  };
  readOnly?: boolean;
};

// Skeleton component
const Skeleton = ({ width, height = "h-4", className = "" }: { width: string; height?: string; className?: string }) => (
  <div className={`${height} ${width} bg-gray-200 dark:bg-gray-700 rounded animate-pulse ${className}`}></div>
);

// Animation constants
const STATUS_SLOT_PX = 96; // reserve badge width to reduce layout jump

const TopNavBar: React.FC<TopNavBarProps> = ({
  title = 'Loading...',
  progress = 0,
  status = "",
  simFlag = false,
  sim_data,
  stats: statsProp,
  totalTasks = 0,
  childEntityType = "Work Items",
  childEntityTypes,
  members = [],
  skills = [],
  duration_with_delay = "",
  cost = 0,
  setShowSkillsModal = () => { },
  handleGoBack = () => { },
  isLoading = false,
  entityType = "Entities",
  integration_data,
  readOnly = false,
  id,
}) => {
  const jwtData: any = useJwt();
  const {
    org_role: userRole = ""
  } = jwtData;

  const containerRef = useRef<HTMLDivElement>(null);
  const rightSideRef = useRef<HTMLDivElement>(null);

  const roadmapState = useSelector((state: any) => state.roadmap);
  const { currentEntity } = useSelector((state: any) => state.timeline);
  const { template_id } = useSelector((state: any) => state.templates);
  const { show_cost: showCost } = useSelector((state: any) => state.organization.orgDetails);
  const isTemplate = !!template_id;

  const displayStatus = (() => {
    if (simFlag) return (sim_data?.status ?? roadmapState?.status ?? '');
    return status ?? roadmapState?.status ?? '';
  })();

  const displayProgress = (() => {
    if (simFlag) return (typeof sim_data?.progress === 'number' ? sim_data.progress : (typeof roadmapState?.progress === 'number' ? roadmapState.progress : 0));
    return typeof progress === 'number' ? progress : (typeof roadmapState?.progress === 'number' ? roadmapState.progress : 0);
  })();

  const effectiveMembers: any[] = (Array.isArray(members) && members.length > 0)
    ? members
    : (Array.isArray(roadmapState?.members) ? roadmapState.members : []);

  const effectiveSkills: any[] = (Array.isArray(skills) && (skills as any[]).length > 0)
    ? (skills as any[])
    : (Array.isArray(roadmapState?.skills_required) ? (roadmapState.skills_required as any[]) : []);

  const effectiveTotalCount: number = (() => {
    const countFromProps = typeof totalTasks === 'number' ? totalTasks : undefined;
    const countFromStats = (() => {
      const s = roadmapState?.stats || {};
      return typeof s.total === 'number' ? s.total : undefined;
    })();
    return (countFromProps ?? countFromStats ?? 0) as number;
  })();

  const isAtRisk = (displayStatus || '').toLowerCase() === DashboardStatus.AT_RISK
    || (displayStatus || '').toLowerCase() === DashboardStatus.DELAYED
    || (displayStatus || '').toLowerCase() === DashboardStatus.UNALLOCATED
    || (displayStatus || '').toLowerCase() === DashboardStatus.BLOCKED;

  const riskBreakdown = React.useMemo(() => {
    const statsFromSim = simFlag && sim_data?.stats ? sim_data.stats : undefined;
    const candidate = (statsFromSim && Object.keys(statsFromSim).length > 0)
      ? statsFromSim
      : (statsProp && Object.keys(statsProp).length > 0)
        ? statsProp
        : (roadmapState?.stats || {});
    const s = candidate || {};
    const delayed = Number(s?.delayed) || 0;
    const unallocated = Number(s?.unallocated) || 0;
    const blocked = Number(s?.blocked) || 0;
    return { delayed, unallocated, blocked, total: delayed + unallocated + blocked };
  }, [simFlag, sim_data?.stats, statsProp, roadmapState?.stats]);



  const isJiraRoadmap = (integration_data?.integration_type || '').toLowerCase() === 'jira';
  const shouldShowStatusBadge = !isTemplate && Boolean(displayStatus) && (entityType !== 'ROADMAP' || !isJiraRoadmap);
  const shouldShowStatusSkeleton = !isTemplate && (entityType !== 'ROADMAP' || !isJiraRoadmap);



  if (isLoading) {
    return (
      <div className="flex items-center justify-between w-full py-2 px-4 bg-light-surfacePrimary dark:bg-secondary-dark border-b border-light-border-subtle dark:border-gray-700 h-[70px] shadow-sm">
        <div className="flex items-center space-x-3 h-[60px] min-w-0 flex-1">
          <button
            onClick={handleGoBack}
            className="p-2 rounded-lg hover:bg-light-interactive-hover dark:hover:bg-tertiary-dark transition-colors duration-200 flex-shrink-0"
          >
            <HiArrowLeft className="text-xl text-light-text-secondary dark:text-gray-300" />
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Status badge skeleton - follow same visibility rule except ignoring status presence */}
            {shouldShowStatusSkeleton && (
              <Skeleton width="w-20" height="h-5" className="rounded-lg flex-shrink-0" />
            )}

            {/* Title skeleton */}
            <Skeleton width="w-52" height="h-5" className="min-w-0 max-w-[300px]" />

            {entityType === 'PROJECT' && (
              <Skeleton width="w-24" height="h-5" className="rounded-lg flex-shrink-0" />
            )}
          </div>
        </div>

        <div className="flex items-center space-x-6 flex-shrink-0">
          {/* Overall Progress Section */}
          <div className="flex flex-col items-end mt-1 h-[60px]">
            <div className="flex flex-row items-center justify-between w-full">
              <span className="text-sm text-light-text-tertiary dark:text-gray-400">Overall Progress</span>
              <Skeleton width="w-8" height="h-4" />
            </div>
            <div className="h-full flex items-center justify-center">
              <div className="w-48 h-2 bg-light-surfaceSecondary dark:bg-gray-700 rounded-full mt-1">
                <Skeleton width="w-0" height="h-2" className="rounded-full" />
              </div>
            </div>
          </div>

          {userRole === "admin" && showCost && (
            <div className="flex flex-col items-end min-w-[100px] h-[60px]">
              <span className="text-sm text-light-text-tertiary dark:text-gray-400">Cost</span>
              <div className="flex h-full items-center">
                <Skeleton width="w-16" height="h-5" />
              </div>
            </div>
          )}

          {/* Duration Section - Only show for projects */}
          {entityType === "PROJECT" && (
            <div className="flex flex-col items-end min-w-[100px] h-[60px]">
              <span className="text-sm text-light-text-tertiary dark:text-gray-400"> Total Duration</span>
              <div className="flex h-full items-center">
                <Skeleton width="w-16" height="h-5" />
              </div>
            </div>
          )}

          {/* Total Entities Section */}
          <div className="flex flex-col items-end min-w-[80px] h-[60px]">
            <span className="text-sm text-light-text-tertiary dark:text-gray-400">Total {childEntityType}</span>
            <div className="flex h-full items-center">
              <Skeleton width="w-8" height="h-5" />
            </div>
          </div>

          {/* Members Section - Always show */}
          {!isTemplate && (
            <div className="flex flex-col items-start h-[60px]">
              <span className="text-sm text-light-text-tertiary dark:text-gray-400 pl-1">Members</span>
              <div className="flex justify-start -space-x-2">
                <Skeleton width="w-9" height="h-9" className="rounded-full border-2 border-light-surfacePrimary dark:border-slate-800" />
                <Skeleton width="w-9" height="h-9" className="rounded-full border-2 border-light-surfacePrimary dark:border-slate-800" />
                <span className="w-9 h-9 rounded-full border-2 border-light-surfacePrimary dark:border-slate-800 bg-light-surfaceSecondary dark:bg-gray-700 text-light-text-muted dark:text-gray-400 text-xs font-semibold flex items-center justify-center shadow-sm"></span>
              </div>
            </div>
          )}

          {/* Skills Section - Only show for projects */}
          {entityType === "PROJECT" && (
            <div className="flex flex-col items-start h-[60px]">
              <div className="flex flex-row items-center justify-between w-full">
                <span className="text-sm text-light-text-tertiary dark:text-gray-400">Skills</span>
                {/* No edit icon in skeleton state */}
              </div>
              <div className="flex gap-1.5 items-center space-x-2 w-[150px] h-full">
                <div className="flex gap-1.5 flex-wrap overflow-auto">
                  <Skeleton width="w-20" height="h-6" className="rounded-2xl" />
                  <Skeleton width="w-8" height="h-6" className="rounded-2xl" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const formattedRiskDetails = [
    { type: 'blocked', count: riskBreakdown.blocked, entityType: childEntityType || 'item' },
    { type: 'delayed', count: riskBreakdown.delayed, entityType: childEntityType || 'item' },
    { type: 'unallocated', count: riskBreakdown.unallocated, entityType: childEntityType || 'item' },
  ].filter(r => r.count > 0) as any[];

  return (
    <div
      ref={containerRef}
      className="flex items-center justify-between w-full py-2 px-4 bg-light-surfacePrimary dark:bg-secondary-dark border-b border-light-border-subtle dark:border-gray-700 h-[70px] shadow-sm"
    >
      <div className="flex items-center space-x-3 h-[60px] min-w-0 flex-1 w-[40%]">
        {!readOnly && (
          <button
            onClick={handleGoBack}
            className="p-2 rounded-lg hover:bg-light-interactive-hover dark:hover:bg-tertiary-dark flex-shrink-0 transition-colors duration-200"
          >
            <HiArrowLeft className="text-xl text-light-text-secondary dark:text-gray-300" />
          </button>
        )}

        <div className="flex items-center gap-2 min-w-0 flex-1 w-full">
          {shouldShowStatusBadge &&
            (isAtRisk ? (
              <RiskStatusDropdown
                entityName={title}
                entityType={(entityType || "work_unit") as any}
                entityId={id || ""}
                riskDetails={formattedRiskDetails}
                status={displayStatus}
              >
                <DashboardStatusBadge
                  status={displayStatus}
                  withRiskHighlight={true}
                  badgeClassName="text-[10px] px-2 py-1"
                />
              </RiskStatusDropdown>
            ) : (
              <span
                className={`relative inline-flex items-center justify-center px-2 py-1 rounded-lg text-white text-xs flex-shrink-0 whitespace-nowrap ${getDashboardStatusColor(displayStatus)} shadow-sm transition-all duration-300`}
              >
                <CrossfadeTextShared
                  text={getDashboardStatusLabel(displayStatus)}
                />
              </span>
            ))}

          <ViewTitle
            name={title}
            entityType={
              currentEntity == ENTITY.PROJECT
                ? roadmapState.projectApiPayload?.entity_type
                : roadmapState.singleRoadmapRes?.entity_type
            }
            type={
              currentEntity == ENTITY.PROJECT
                ? roadmapState.projectApiPayload?.type
                : roadmapState.singleRoadmapRes?.type
            }
            parent_info={
              currentEntity == ENTITY.PROJECT
                ? roadmapState.projectApiPayload?.parent_info
                : roadmapState.singleRoadmapRes?.parent_info
            }
            integration_data={integration_data}
            createdAt={
              currentEntity == ENTITY.PROJECT
                ? roadmapState.projectApiPayload?.created_at
                : roadmapState.singleRoadmapRes?.created_at
            }
            createdBy={
              currentEntity == ENTITY.PROJECT
                ? roadmapState.projectApiPayload?.created_by
                : roadmapState.singleRoadmapRes?.created_by
            }
            id={
              currentEntity == ENTITY.PROJECT
                ? roadmapState.projectApiPayload?.id
                : roadmapState.singleRoadmapRes?.id
            }
          />
        </div>
      </div>

      <div
        ref={rightSideRef}
        className="flex items-center space-x-6 flex-shrink-0 ml-[30px]"
      >
        <div className="flex flex-col items-end h-[60px]">
          <div className="flex flex-row items-center justify-between w-full">
            <span className="text-sm text-light-text-tertiary dark:text-gray-400">
              Overall Progress
            </span>
            <AnimatedNumberShared
              value={Number(displayProgress) || 0}
              className="text-base font-medium text-light-text-primary dark:text-white"
              formatter={(n) => `${n}%`}
            />
          </div>
          <div className="h-full flex items-center justify-center">
            <div className="w-48 h-2 bg-light-surfaceSecondary dark:bg-gray-700 rounded-full mt-1">
              <div
                className="h-2 bg-primary-light rounded-full transition-all duration-300"
                style={{ width: `${displayProgress}%` }}
              ></div>
            </div>
          </div>
        </div>

        {userRole === "admin" && showCost ? (
          <div className="flex flex-col items-end  h-[60px] m-0 max-w-[120px] min-w-[10px]">
            <span className="text-sm text-light-text-tertiary dark:text-gray-400">
              Cost
            </span>
            <AnimatedNumberShared
              value={Number(cost) || 0}
              className="flex h-full items-center font-medium text-light-text-primary dark:text-white text-center"
              formatter={(n) =>
                new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 0,
                }).format(n)
              }
            />
          </div>
        ) : (
          ""
        )}

        {currentEntity === ENTITY.PROJECT && (
          <div className="flex flex-col items-end min-w-[100px] h-[60px]">
            <span className="text-sm text-light-text-tertiary dark:text-gray-400">
              {" "}
              Total Duration
            </span>
            <AnimatedNumberShared
              value={Number(duration_with_delay) || 0}
              className="flex h-full items-center font-medium text-light-text-primary dark:text-white text-center"
              formatter={(n) => `${n} Days`}
            />
          </div>
        )}

        {(() => {
          const tooltipContent = getWorkItemBreakdownTooltipContent(
            childEntityTypes,
            "Total work items count",
          );

          return (
            <Tooltip placement="top" content={tooltipContent}>
              <div className="flex flex-col items-end min-w-[80px] h-[60px] cursor-help">
                <span className="text-sm text-light-text-tertiary dark:text-gray-400">
                  Total {childEntityType}
                </span>
                <AnimatedNumberShared
                  value={Number(effectiveTotalCount) || 0}
                  className="flex h-full items-center font-medium text-light-text-primary dark:text-white"
                  formatter={(n) => n.toString().padStart(2, "0")}
                />
              </div>
            </Tooltip>
          );
        })()}

        {!isTemplate && (
          <div className="flex flex-col items-start space-x-2 h-[60px]">
            <span className="text-sm text-light-text-tertiary dark:text-gray-400 pl-2">
              Members
            </span>
            {effectiveMembers.length > 0 ? (
              <Resources resources={effectiveMembers} projectId={""} />
            ) : (
              <Tooltip content="No member assigned" placement="bottom">
                <div className="flex items-center justify-start -space-x-2">
                  <div className="w-9 h-9 rounded-full border-2 border-dashed border-light-border-default dark:border-gray-600 bg-light-surfaceSecondary dark:bg-gray-800 flex items-center justify-center hover:bg-light-interactive-hover dark:hover:bg-gray-700 transition-colors duration-200">
                    <span className="text-xs text-light-text-muted dark:text-gray-500">
                      +
                    </span>
                  </div>
                  <div className="w-9 h-9 rounded-full border-2 border-dashed border-light-border-default dark:border-gray-600 bg-light-surfaceSecondary dark:bg-gray-800 flex items-center justify-center opacity-60 hover:opacity-80 transition-opacity duration-200">
                    <span className="text-xs text-light-text-muted dark:text-gray-500">
                      +
                    </span>
                  </div>
                  <span className="w-9 h-9 rounded-full border-2 border-dashed border-light-border-default dark:border-gray-600 bg-light-surfaceSecondary dark:bg-gray-800 text-light-text-muted dark:text-gray-500 text-xs font-medium flex items-center justify-center opacity-30">
                    +
                  </span>
                </div>
              </Tooltip>
            )}
          </div>
        )}

        {currentEntity === ENTITY.PROJECT && (
          <div className="flex flex-col items-start h-[60px]">
            <div className="flex flex-row items-center justify-between w-full">
              <span className="text-sm text-light-text-tertiary dark:text-gray-400">
                Skills
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setShowSkillsModal(true)}
                  className="text-sm text-light-text-secondary dark:text-gray-400 cursor-pointer hover:text-light-text-primary dark:hover:text-gray-300 transition-colors duration-200"
                >
                  <HiPencilAlt className="text-light-text-secondary dark:text-gray-400 text-[20px] mr-1 hover:text-light-text-primary dark:hover:text-gray-300 transition-colors duration-200" />
                </button>
              )}
            </div>
            <div className="flex gap-1.5 items-center space-x-2 w-[150px] h-full">
              <div className=" flex gap-1.5 flex-wrap overflow-auto">
                {effectiveSkills.length === 0 ? (
                  <CustomTooltip title="No skills added" placement="bottom">
                    <span className="px-8 h-6 rounded-2xl border-2 border-dashed border-light-border-default dark:border-gray-600 bg-light-surfaceSecondary dark:bg-gray-800 text-light-text-muted dark:text-gray-500 text-xs font-medium flex items-center justify-center opacity-70">
                      +
                    </span>
                  </CustomTooltip>
                ) : (
                  <>
                    {effectiveSkills.slice(0, 1).map((skill: any) => {
                      return (
                        <CustomTooltip
                          title={skill?.name}
                          placement="top"
                          key={skill?.id || skill?.name}
                        >
                          <span
                            className={`flex h-fit flex-wrap items-center flex-col gap-1 font-semibold group-hover:bg-primary-light dark:group-hover:bg-primary-dark rounded-2xl py-1 p-2 text-xs shadow-sm transition-all duration-200 cursor-pointer`}
                            style={{
                              background: skill?.skill_color,
                              color: "#fff",
                            }}
                          >
                            <span className="max-w-[80px] truncate">
                              {skill?.name}
                            </span>
                          </span>
                        </CustomTooltip>
                      );
                    })}
                    {effectiveSkills.length - 1 > 0 && (
                      <CustomTooltip
                        title={`Additional skills: ${effectiveSkills
                          .slice(1)
                          .map((s: any) => s?.name)
                          .filter(Boolean)
                          .join(", ")}`}
                        placement="top"
                      >
                        <span className="flex h-fit items-center gap-1 font-semibold text-light-text-primary dark:text-gray-200 bg-light-surfaceSecondary dark:bg-tertiary-dark rounded-2xl py-1 p-2 text-xs border border-light-border-subtle dark:border-gray-600 shadow-sm transition-transform duration-200 cursor-pointer">
                          <span>{`+${effectiveSkills.length - 1}`}</span>
                        </span>
                      </CustomTooltip>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopNavBar; 